import { Injectable } from "@angular/core";
import { IgxGridComponent } from '@infragistics/igniteui-angular/grids/grid';
import { ISortingExpression, SortingDirection } from '@infragistics/igniteui-angular/core';
import { StringHelper, Utilities } from "@tylertech/shared-module";
import { IGridViewMetadata } from "@tylertech/tyler-api-types";
import { ResetStateService } from "../reset.state.service";
import { ObjectHelpers } from "../../utilities";

@Injectable({ providedIn: "root" })
export class GridSortService extends ResetStateService
{
    SortingExpressions: Record<string, ISortingExpression[]> = {};

    InitialSortDone: Record<string, boolean> = {};

    initializeSort(providerId: string, skipSort: boolean, metadata: IGridViewMetadata, grid: IgxGridComponent): void
    {
        if (!this.InitialSortDone[providerId] && !skipSort)
            this.setInitialSort(providerId, metadata, grid);
        if (skipSort)
            grid.sortingExpressions = [];
        if (!this.SortingExpressions[providerId] || grid.sortingExpressions?.length > 0)
            this.SortingExpressions[providerId] = grid.sortingExpressions;
    }

    private setInitialSort(providerId: string, metadata: IGridViewMetadata, grid: IgxGridComponent): void
    {
        if (
            Utilities.IsNullOrUndefined(metadata.GridProperties.Sort) ||
            metadata.GridProperties.Sort.Name === "Sort_IgnoreSort" ||
            grid.sortingExpressions?.length > 0
        )
        {
            this.InitialSortDone[providerId] = true;
            if (!this.SortingExpressions[providerId] || grid.sortingExpressions?.length > 0)
                this.SortingExpressions[providerId] = grid.sortingExpressions;
            return;
        }
        this.SortingExpressions[providerId] = [];
        let values = metadata.GridProperties.Sort.Name.split(".");
        this.SortingExpressions[providerId].push(<ISortingExpression>{
            fieldName: values[values.length - 1],
            dir: metadata.GridProperties.Sort.Direction === "DESC" ? 2 : 1,
            ignoreCase: true,
        });
        if (metadata.GridProperties.SecondarySort)
        {
            values = metadata.GridProperties.SecondarySort.Name.split(".");
            this.SortingExpressions[providerId].push(<ISortingExpression>{
                fieldName: values[values.length - 1],
                dir: metadata.GridProperties.SecondarySort.Direction === "DESC" ? 2 : 1,
                ignoreCase: true,
            });
        }
        this.InitialSortDone[providerId] = true;
        grid.sortingExpressions = this.SortingExpressions[providerId];
    }

    fetchOrder(providerId: string): string
    {
        if (!Utilities.IsNullOrUndefined(this.SortingExpressions[providerId]))
        {
            const expressions: string[] = [];
            this.SortingExpressions[providerId].forEach(e =>
            {
                expressions.push(`${e.fieldName} ${e.dir === SortingDirection.Desc ? "DESC" : "ASC"}`);
            });
            return expressions.join(",");
        }
        return null;
    }

    sort(providerId: string, rows: any[], viewMetadata: IGridViewMetadata): any[]
    {
        const self = this;
        if (!Utilities.IsNullOrUndefined(this.SortingExpressions[providerId]) && !this.isAddingOrEditing(viewMetadata) && rows?.length > 0)
        {
            this.SortingExpressions[providerId]
                .slice()
                .reverse()
                .forEach(e =>
                {
                    let entitySort = false;
                    if (!(e.fieldName in rows[0]))
                        entitySort = true;

                    const property = viewMetadata.GridProperties.ClassMetadata.Properties.find(x => x.Name === e.fieldName);
                    const isDate = property
                        && (property.InputType === "Date"
                            || property.InputType === "DateTime"
                            || property.InputType === "Time");

                    rows.sort((a, b) =>
                    {
                        if (entitySort)
                        {
                            a = a["$$model"];
                            b = b["$$model"];
                        }
                        const ret = self._sort(a, b, e.fieldName, e.dir === SortingDirection.Desc ? "DESC" : "ASC", isDate);
                        return ret;
                    });
                });
        }
        return rows;
    }

    _sort(a: any, b: any, field: string, sortDirection: string, isDate: boolean): number
    {
        let ret = this.compare(a, b, field, isDate);
        if (sortDirection === "DESC")
        {
            ret *= -1;
        }
        return ret;
    }

    compare(a: any, b: any, field: string, isDate: boolean): number
    {
        const aVal = this.getValue(a, field);
        const bVal = this.getValue(b, field);

        const aIsNull = Utilities.IsNullOrUndefined(aVal[1]);
        const bIsNull = Utilities.IsNullOrUndefined(bVal[1]);

        if (aIsNull && bIsNull) return 0;           // Both null = equal
        if (aIsNull) return 1;                      // Null sorts to end
        if (bIsNull) return -1;                     // Non-null sorts before null

        for (let i: number = 0; i < aVal.length; i++) {
            if (typeof aVal[i] === typeof bVal[i] && aVal[i] !== bVal[i]) {
                if (isDate)
                    return new Date(aVal[1]).getTime() - new Date(bVal[1]).getTime() > 0 ? 1 : -1;
                return aVal[i] > bVal[i] ? 1 : -1;
            }
        }
        return 0;
    }

    private getValue(row: any, field: string): any[]
    {
        const cell = row[field];
        if (Utilities.IsNullOrUndefined(cell))
        {
            return [0, cell, cell];
        }
        let value: string;
        if (ObjectHelpers.isNumber(cell))
            value = StringHelper.parseNumber(cell.toString()).toString();
        else
            value = cell;

        if (!Number.isNaN(Number(value)))
        {
            return [0, Number(value), cell.toString()];
        }
        if (typeof value === "string")
        {
            return [0, value.toLowerCase(), cell.toString()
                .toLowerCase()];
        }
        return [1, value, cell.toString()
            .toLowerCase()];
    }

    private isAddingOrEditing(viewMetadata: IGridViewMetadata): boolean
    {
        return viewMetadata["$$cellEditing"] || viewMetadata["$$cellAdding"];
    }
}
